import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme/ThemeContext';
import type { Laboratory } from '../calendar/types';

export interface PaymentReportDetailsState {
  enabled: boolean;
  sourceType: 'treatment' | 'appointment';
  sourceId: string;
  treatmentId: string;
  primaryDoctorId: string;
  secondDoctorId: string;
  primaryDoctorCommissionPercentage: string;
  secondDoctorCommissionPercentage: string;
  grossPrice: string;
  invoice: boolean;
  discountMode: 'amount' | 'percent';
  discountValue: string;
  specialMaterialsCost: string;
  additionalUseFee: string;
  isSpecialty: boolean;
  treatedTeeth: string;
  laboratoryId: string;
  laboratoryCost: string;
  mechanicName: string;
}

export interface PaymentReportTreatmentOption {
  id: number;
  name: string;
  invoice?: boolean;
  discountMode?: 'amount' | 'percent';
  discountValue?: number;
  specialMaterialsCost?: number;
  laboratoryId?: number | null;
  laboratoryCost?: number;
  mechanicName?: string | null;
  doctorId?: number | null;
  secondDoctorId?: number | null;
  primaryDoctorCommissionPercentage?: number | null;
  secondDoctorCommissionPercentage?: number | null;
  grossPrice?: number | null;
  recommendedPrice?: number | null;
  additionalUseFee?: number;
  isSpecialty?: boolean;
  treatedTeeth?: string | null;
}

export interface ReportDoctorOption { id: number; firstName: string; lastName: string; commissionPercentage?: number | null }

export interface ReportReadinessIssue {
  sourceType: 'treatment' | 'appointment';
  sourceId: number;
  patientId: number;
  patientName: string;
  sourceName: string;
  paymentIds: number[];
  missingFields: string[];
  details: Partial<{
    primaryDoctorId: number | null;
    secondDoctorId: number | null;
    primaryDoctorCommissionPercentage: number | null;
    secondDoctorCommissionPercentage: number | null;
    grossPrice: number | null;
    discountMode: 'amount' | 'percent' | null;
    discountValue: number | null;
    invoice: boolean | null;
    additionalUseFee: number | null;
    specialMaterialsCost: number | null;
    isSpecialty: boolean | null;
    treatedTeeth: string | null;
    laboratoryId: number | null;
    laboratoryCost: number | null;
    mechanicName: string | null;
  }>;
}

export const emptyPaymentReportDetails = (): PaymentReportDetailsState => ({
  enabled: false,
  sourceType: 'treatment',
  sourceId: '',
  treatmentId: '',
  primaryDoctorId: '',
  secondDoctorId: '',
  primaryDoctorCommissionPercentage: '',
  secondDoctorCommissionPercentage: '',
  grossPrice: '',
  invoice: false,
  discountMode: 'amount',
  discountValue: '',
  specialMaterialsCost: '',
  additionalUseFee: '',
  isSpecialty: false,
  treatedTeeth: '',
  laboratoryId: '',
  laboratoryCost: '',
  mechanicName: ''
});

export const paymentReportDetailsPayload = (value: PaymentReportDetailsState) => value.enabled ? {
  treatmentId: Number(value.treatmentId),
  invoice: value.invoice,
  discountMode: value.discountMode,
  discountValue: Number(value.discountValue || 0),
  specialMaterialsCost: Number(value.specialMaterialsCost || 0),
  laboratoryId: value.laboratoryId ? Number(value.laboratoryId) : null,
  laboratoryCost: Number(value.laboratoryCost || 0),
  mechanicName: value.mechanicName.trim() || null
} : null;

const optionalNumber = (value: string) => value.trim() === '' ? null : Number(value);

export const reportSourceDetailsPayload = (input: PaymentReportDetailsState | PaymentReportDetailsState[]) => {
  const values = Array.isArray(input) ? input : [input];
  return values.filter(value => value.enabled && (value.sourceId || value.treatmentId)).map(value => ({
    sourceType: value.sourceType,
    sourceId: Number(value.sourceId || value.treatmentId),
    primaryDoctorId: optionalNumber(value.primaryDoctorId),
    secondDoctorId: optionalNumber(value.secondDoctorId),
    primaryDoctorCommissionPercentage: optionalNumber(value.primaryDoctorCommissionPercentage),
    secondDoctorCommissionPercentage: optionalNumber(value.secondDoctorCommissionPercentage),
    grossPrice: optionalNumber(value.grossPrice),
    discountMode: value.discountMode,
    discountValue: Number(value.discountValue || 0),
    invoice: value.invoice,
    additionalUseFee: Number(value.additionalUseFee || 0),
    specialMaterialsCost: Number(value.specialMaterialsCost || 0),
    isSpecialty: value.isSpecialty,
    treatedTeeth: value.treatedTeeth.trim() || null,
    laboratoryId: optionalNumber(value.laboratoryId),
    laboratoryCost: Number(value.laboratoryCost || 0),
    mechanicName: value.mechanicName.trim() || null
  }));
};

export const paymentReportDetailsFromIssue = (issue: ReportReadinessIssue): PaymentReportDetailsState => {
  const details = issue.details || {};
  return {
    ...emptyPaymentReportDetails(),
    enabled: true,
    sourceType: issue.sourceType,
    sourceId: String(issue.sourceId),
    treatmentId: issue.sourceType === 'treatment' ? String(issue.sourceId) : '',
    primaryDoctorId: details.primaryDoctorId == null ? '' : String(details.primaryDoctorId),
    secondDoctorId: details.secondDoctorId == null ? '' : String(details.secondDoctorId),
    primaryDoctorCommissionPercentage: details.primaryDoctorCommissionPercentage == null ? '' : String(details.primaryDoctorCommissionPercentage),
    secondDoctorCommissionPercentage: details.secondDoctorCommissionPercentage == null ? '' : String(details.secondDoctorCommissionPercentage),
    grossPrice: details.grossPrice == null ? '' : String(details.grossPrice),
    discountMode: details.discountMode || 'amount',
    discountValue: details.discountValue ? String(details.discountValue) : '',
    invoice: !!details.invoice,
    additionalUseFee: details.additionalUseFee ? String(details.additionalUseFee) : '',
    specialMaterialsCost: details.specialMaterialsCost ? String(details.specialMaterialsCost) : '',
    isSpecialty: !!details.isSpecialty,
    treatedTeeth: details.treatedTeeth || '',
    laboratoryId: details.laboratoryId == null ? '' : String(details.laboratoryId),
    laboratoryCost: details.laboratoryCost ? String(details.laboratoryCost) : '',
    mechanicName: details.mechanicName || ''
  };
};

interface Props {
  value: PaymentReportDetailsState;
  onChange: (next: PaymentReportDetailsState) => void;
  treatments: PaymentReportTreatmentOption[];
  laboratories: Laboratory[];
  doctors?: ReportDoctorOption[];
  required?: boolean;
  title?: string;
}

export const PaymentReportDetailsFields: React.FC<Props> = ({ value, onChange, treatments, laboratories, doctors = [], required = false, title }) => {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const tt = (key: string, fallback: string) => t(`calendar.paymentReport.${key}`, fallback);
  const input: React.CSSProperties = { padding: '9px 10px', border: `1px solid ${tokens.app.inputBorder}`, borderRadius: 6, background: tokens.app.input, color: tokens.app.text, colorScheme: tokens.isLight ? 'light' : 'dark', minWidth: 0 };
  const label: React.CSSProperties = { display: 'grid', gap: 5, color: tokens.app.muted, fontSize: 12, fontWeight: 700 };

  if (treatments.length === 0 && value.sourceType === 'treatment' && !value.sourceId) return null;

  const withTreatment = (treatmentId: string, base: PaymentReportDetailsState = value): PaymentReportDetailsState => {
    const treatment = treatments.find(item => String(item.id) === treatmentId);
    return {
      ...base,
      sourceType: 'treatment',
      sourceId: treatmentId,
      treatmentId,
      primaryDoctorId: treatment?.doctorId == null ? '' : String(treatment.doctorId),
      secondDoctorId: treatment?.secondDoctorId == null ? '' : String(treatment.secondDoctorId),
      primaryDoctorCommissionPercentage: treatment?.primaryDoctorCommissionPercentage == null ? '' : String(treatment.primaryDoctorCommissionPercentage),
      secondDoctorCommissionPercentage: treatment?.secondDoctorCommissionPercentage == null ? '' : String(treatment.secondDoctorCommissionPercentage),
      grossPrice: treatment?.grossPrice == null && treatment?.recommendedPrice == null ? '' : String(treatment.grossPrice ?? treatment.recommendedPrice),
      invoice: treatment?.invoice || false,
      discountMode: treatment?.discountMode || 'amount',
      discountValue: treatment?.discountValue ? String(treatment.discountValue) : '',
      specialMaterialsCost: treatment?.specialMaterialsCost ? String(treatment.specialMaterialsCost) : '',
      additionalUseFee: treatment?.additionalUseFee ? String(treatment.additionalUseFee) : '',
      isSpecialty: treatment?.isSpecialty || false,
      treatedTeeth: treatment?.treatedTeeth || '',
      laboratoryId: treatment?.laboratoryId ? String(treatment.laboratoryId) : '',
      laboratoryCost: treatment?.laboratoryCost ? String(treatment.laboratoryCost) : '',
      mechanicName: treatment?.mechanicName || ''
    };
  };

  const chooseTreatment = (treatmentId: string) => onChange(withTreatment(treatmentId));

  return <section style={{ padding: 12, border: `1px solid ${tokens.app.border}`, borderRadius: 8, background: tokens.app.surface }}>
    <label className="responsive-row" style={{ display: 'flex', gap: 8, alignItems: 'center', color: tokens.app.text, fontWeight: 700 }}>
      {!required && <input
        type="checkbox"
        checked={value.enabled}
        onChange={event => {
          const enabled = event.target.checked;
          const treatmentId = value.treatmentId || (treatments.length === 1 ? String(treatments[0].id) : '');
          const next = { ...value, enabled, treatmentId };
          onChange(enabled && treatmentId ? withTreatment(treatmentId, next) : next);
        }}
      />}
      {title || (required ? tt('requiredDetails', 'Report details required') : tt('addDetails', 'Add report details'))}
    </label>
    {value.enabled && <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
      <div style={{ color: tokens.app.muted, fontSize: 12 }}>{tt('help', 'Details update treatment receiving this payment and feed doctor reports.')}</div>
      <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
        {value.sourceType === 'treatment' && treatments.length > 0 && <label style={label}>{tt('treatment', 'Treatment')}
          <select style={input} value={value.treatmentId} onChange={event => chooseTreatment(event.target.value)} required>
            <option value="">{tt('selectTreatment', 'Select treatment')}</option>
            {treatments.map(treatment => <option key={treatment.id} value={treatment.id}>{treatment.name}</option>)}
          </select>
        </label>}
        <label style={label}>{tt('doctor', 'Doctor')}
          <select style={input} value={value.primaryDoctorId} onChange={event => {
            const doctor = doctors.find(item => String(item.id) === event.target.value);
            onChange({ ...value, primaryDoctorId: event.target.value, primaryDoctorCommissionPercentage: doctor?.commissionPercentage == null ? value.primaryDoctorCommissionPercentage : String(doctor.commissionPercentage) });
          }} required>
            <option value="">{tt('selectDoctor', 'Select doctor')}</option>
            {doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
          </select>
        </label>
        <label style={label}>{tt('doctorCommission', 'Doctor share %')}
          <input style={input} type="number" min="0" max="100" step="0.01" value={value.primaryDoctorCommissionPercentage} onChange={event => onChange({ ...value, primaryDoctorCommissionPercentage: event.target.value })} required />
        </label>
        <label style={label}>{tt('assistant', 'Second doctor / assistant')}
          <select style={input} value={value.secondDoctorId} onChange={event => {
            const doctor = doctors.find(item => String(item.id) === event.target.value);
            onChange({ ...value, secondDoctorId: event.target.value, secondDoctorCommissionPercentage: event.target.value ? (doctor?.commissionPercentage == null ? value.secondDoctorCommissionPercentage : String(doctor.commissionPercentage)) : '' });
          }}>
            <option value="">{tt('noAssistant', 'No assistant')}</option>
            {doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
          </select>
        </label>
        <label style={label}>{tt('assistantCommission', 'Assistant share %')}
          <input style={input} type="number" min="0" max="100" step="0.01" value={value.secondDoctorCommissionPercentage} onChange={event => onChange({ ...value, secondDoctorCommissionPercentage: event.target.value })} disabled={!value.secondDoctorId} required={!!value.secondDoctorId} />
        </label>
        {value.sourceType === 'treatment' && <label style={label}>{tt('grossPrice', 'Gross price')}
          <input style={input} type="number" min="0" step="0.01" value={value.grossPrice} onChange={event => onChange({ ...value, grossPrice: event.target.value })} />
        </label>}
        <label className="responsive-row" style={{ ...label, display: 'flex', alignItems: 'center', alignSelf: 'end', minHeight: 38, flexDirection: 'row' }}>
          <input type="checkbox" checked={value.invoice} onChange={event => onChange({ ...value, invoice: event.target.checked })} />
          {tt('invoice', 'Has invoice')}
        </label>
        <label style={label}>{tt('discountType', 'Discount type')}
          <select style={input} value={value.discountMode} onChange={event => onChange({ ...value, discountMode: event.target.value as 'amount' | 'percent' })}>
            <option value="amount">{tt('fixedAmount', 'Fixed amount')}</option>
            <option value="percent">{tt('percentage', 'Percentage')}</option>
          </select>
        </label>
        <label style={label}>{tt('discount', 'Discount')}
          <input style={input} type="number" min="0" max={value.discountMode === 'percent' ? '100' : undefined} step="0.01" value={value.discountValue} onChange={event => onChange({ ...value, discountValue: event.target.value })} />
        </label>
        <label style={label}>{tt('specialMaterials', 'Additional materials cost')}
          <input style={input} type="number" min="0" step="0.01" value={value.specialMaterialsCost} onChange={event => onChange({ ...value, specialMaterialsCost: event.target.value })} />
        </label>
        <label style={label}>{tt('additionalUseFee', 'Additional-use fee')}
          <input style={input} type="number" min="0" step="0.01" value={value.additionalUseFee} onChange={event => onChange({ ...value, additionalUseFee: event.target.value })} />
        </label>
        <label className="responsive-row" style={{ ...label, display: 'flex', alignItems: 'center', alignSelf: 'end', minHeight: 38, flexDirection: 'row' }}>
          <input type="checkbox" checked={value.isSpecialty} onChange={event => onChange({ ...value, isSpecialty: event.target.checked })} />
          {tt('specialty', 'Specialty')}
        </label>
        <label style={label}>{tt('treatedTeeth', 'Treated teeth')}
          <input style={input} value={value.treatedTeeth} onChange={event => onChange({ ...value, treatedTeeth: event.target.value })} />
        </label>
        <label style={label}>{tt('laboratory', 'Laboratory')}
          <select style={input} value={value.laboratoryId} onChange={event => onChange({ ...value, laboratoryId: event.target.value })}>
            <option value="">{tt('noLaboratory', 'No laboratory')}</option>
            {laboratories.map(laboratory => <option key={laboratory.id} value={laboratory.id}>{laboratory.name}</option>)}
          </select>
        </label>
        <label style={label}>{tt('mechanicPayment', 'Laboratory / mechanic payment')}
          <input style={input} type="number" min="0" step="0.01" value={value.laboratoryCost} onChange={event => onChange({ ...value, laboratoryCost: event.target.value })} />
        </label>
        <label style={label}>{tt('mechanic', 'Mechanic / technician')}
          <input style={input} value={value.mechanicName} onChange={event => onChange({ ...value, mechanicName: event.target.value })} />
        </label>
      </div>
    </div>}
  </section>;
};
